import { useEffect, useRef, useState } from 'react'
import config from '../../../config'
import type {
    AdjuntoDTO,
    CreateAdjuntoRequest,
    CreateCRMRequest,
    CRMDTO,
    CRMTextoDTO,
    CRMTextoTipo,
    SiniestroDTO,
    StorageFile,
    WrapperResponse,
} from '../../../model/kartera-api-model'
import APIInvoke from '../../../utils/APIInvoke'
import {
    aseguradoraName,
    claimStatusView,
    errorMessage,
    formatCompactDate,
    labelize,
    NOTE_TYPES,
    noteMeta,
    personName,
    ramoName,
    resolutionView,
    statusKey,
    vehicleLabel,
} from '../common/claim-common'
import type { BitacoraFilter, LoadState } from '../common/claim-common'

export type ClaimDrawerTab = 'general' | 'bitacora' | 'adjuntos'

export interface ClaimDrawerProps {
    siniestroId: number | null
    onClose: () => void
    onEdit?: (siniestroId: number) => void
    onDeleted?: (siniestro?: SiniestroDTO | null) => void
}

export interface ClaimDrawerTabItem {
    id: ClaimDrawerTab
    label: string
    count?: number | null
}

export interface ClaimInsured {
    id: number
    name: string
    parentesco: string | null
    selected: boolean
}

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024
const DETAIL_ERROR = 'No se pudo cargar el detalle del siniestro.'
const DELETE_ERROR = 'No se pudo eliminar el siniestro.'
const BITACORA_ERROR = 'No se pudo cargar la bitácora.'
const ADJUNTOS_ERROR = 'No se pudieron cargar los adjuntos.'
const SAVE_NOTE_ERROR = 'No se pudo guardar la bitácora.'
const UPLOAD_ERROR = 'No se pudo subir el archivo.'

export const useClaimDrawerModel = ({ siniestroId, onClose, onDeleted }: ClaimDrawerProps) => {
    const toastTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const bitacoraLoadedFor = useRef<number | null>(null)
    const adjuntosLoadedFor = useRef<number | null>(null)
    const [siniestro, setSiniestro] = useState<SiniestroDTO | null>(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [reloadToken, setReloadToken] = useState(0)
    const [tab, setTab] = useState<ClaimDrawerTab>('general')
    const [toast, setToast] = useState<string | null>(null)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [bitacora, setBitacora] = useState<CRMTextoDTO[]>([])
    const [bitacoraState, setBitacoraState] = useState<LoadState>('idle')
    const [bitacoraError, setBitacoraError] = useState<string | null>(null)
    const [noteType, setNoteType] = useState<CRMTextoTipo>(NOTE_TYPES[0].id)
    const [note, setNote] = useState('')
    const [savingNote, setSavingNote] = useState(false)
    const [bitacoraFilter, setBitacoraFilter] = useState<BitacoraFilter>('todo')
    const [adjuntos, setAdjuntos] = useState<AdjuntoDTO[]>([])
    const [adjuntosState, setAdjuntosState] = useState<LoadState>('idle')
    const [adjuntosError, setAdjuntosError] = useState<string | null>(null)
    const [uploading, setUploading] = useState(false)
    const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

    const showToast = (message: string) => {
        clearTimeout(toastTimer.current)
        setToast(message)
        toastTimer.current = setTimeout(() => setToast(null), 3000)
    }

    useEffect(() => () => clearTimeout(toastTimer.current), [])

    useEffect(() => {
        if (!siniestroId) return undefined
        setTab('general')
        setSiniestro(null)
        setError(null)
        setConfirmDelete(false)
        setBitacora([])
        setBitacoraState('idle')
        setBitacoraError(null)
        bitacoraLoadedFor.current = null
        setNote('')
        setBitacoraFilter('todo')
        setAdjuntos([])
        setAdjuntosState('idle')
        setAdjuntosError(null)
        adjuntosLoadedFor.current = null
        setConfirmDeleteId(null)
        return undefined
    }, [siniestroId])

    useEffect(() => {
        if (!siniestroId) return undefined
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                setError(null)
                const response = await APIInvoke.invokeGET(`/siniestros/${siniestroId}`)
                const payload: WrapperResponse<SiniestroDTO> | null = await response.json()
                if (!active) return
                if (!payload?.ok || !payload.body) throw new Error(payload?.message || DETAIL_ERROR)
                setSiniestro(payload.body)
            } catch (loadError) {
                if (active) {
                    setSiniestro(null)
                    setError(errorMessage(loadError, DETAIL_ERROR))
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [siniestroId, reloadToken])

    useEffect(() => {
        if (!siniestroId || tab !== 'bitacora' || bitacoraLoadedFor.current === siniestroId) return undefined
        let active = true
        const load = async () => {
            try {
                setBitacoraState('loading')
                setBitacoraError(null)
                const response = await APIInvoke.invokeGET(`/crm?siniestroId=${siniestroId}`)
                const payload: WrapperResponse<CRMDTO[]> | null = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || BITACORA_ERROR)
                const textos = (payload.body || []).flatMap((item) => item.textos || [])
                setBitacora(textos.sort((a, b) => String(b.fecha || '').localeCompare(String(a.fecha || ''))))
                setBitacoraState('ready')
                bitacoraLoadedFor.current = siniestroId
            } catch (loadError) {
                if (active) {
                    setBitacoraState('error')
                    setBitacoraError(errorMessage(loadError, BITACORA_ERROR))
                }
            }
        }
        load()
        return () => { active = false }
    }, [siniestroId, tab])

    useEffect(() => {
        if (!siniestroId || tab !== 'adjuntos' || adjuntosLoadedFor.current === siniestroId) return undefined
        let active = true
        const load = async () => {
            try {
                setAdjuntosState('loading')
                setAdjuntosError(null)
                const response = await APIInvoke.invokeGET(`/adjuntos/siniestros/${siniestroId}`)
                const payload: WrapperResponse<AdjuntoDTO[]> | null = await response.json()
                if (!active) return
                if (!payload?.ok) throw new Error(payload?.message || ADJUNTOS_ERROR)
                setAdjuntos(payload.body || [])
                setAdjuntosState('ready')
                adjuntosLoadedFor.current = siniestroId
            } catch (loadError) {
                if (active) {
                    setAdjuntosState('error')
                    setAdjuntosError(errorMessage(loadError, ADJUNTOS_ERROR))
                }
            }
        }
        load()
        return () => { active = false }
    }, [siniestroId, tab])

    const deleteSiniestro = async () => {
        if (!siniestro?.id || deleting) return
        try {
            setConfirmDelete(false)
            setDeleting(true)
            const payload: WrapperResponse<SiniestroDTO> | null = await (await APIInvoke.invokeDELETE(`/siniestros/${siniestro.id}`)).json()
            if (!payload?.ok) throw new Error(payload?.message || DELETE_ERROR)
            onDeleted?.(siniestro)
            onClose()
        } catch (deleteError) {
            showToast(errorMessage(deleteError, DELETE_ERROR))
        } finally {
            setDeleting(false)
        }
    }

    const openFile = async (path?: string | null) => {
        try {
            const url: string | null | undefined = await APIInvoke.resolveStoredFileUrl(path)
            if (url) window.open(url, '_blank', 'noopener')
        } catch (fileError) {
            showToast(errorMessage(fileError, 'No se pudo abrir el archivo.'))
        }
    }

    const addNote = async () => {
        const texto = note.trim()
        if (!texto || !siniestro) return
        try {
            setSavingNote(true)
            const request: CreateCRMRequest = {
                asunto: 'bitacora',
                tipo: noteType,
                siniestroId: siniestro.id,
                polizaId: siniestro.poliza?.id,
                clienteId: siniestro.poliza?.cliente?.id,
                textos: [{ tipo: noteType, texto }],
            }
            const response = await APIInvoke.invokePOST('/crm', request)
            const payload: WrapperResponse<CRMDTO> | null = await response.json()
            if (!payload?.ok) throw new Error(payload?.message || SAVE_NOTE_ERROR)
            const created = payload.body?.textos?.[0]
            if (created) setBitacora((items) => [created, ...items])
            setNote('')
            setBitacoraFilter('todo')
            showToast('Nota agregada a la bitácora')
        } catch (saveError) {
            showToast(errorMessage(saveError, SAVE_NOTE_ERROR))
        } finally {
            setSavingNote(false)
        }
    }

    const uploadFiles = async (fileList: FileList | null) => {
        const files = Array.from(fileList || [])
        if (!files.length || !siniestro) return
        try {
            setUploading(true)
            const created: AdjuntoDTO[] = []
            for (const file of files) {
                if (file.size > MAX_UPLOAD_BYTES) throw new Error(`${file.name} supera 10 MB.`)
                const body = new FormData()
                body.append('bucket', config.bucket.name)
                body.append('path', `siniestros/${siniestro.id}/${file.name}`)
                body.append('file', file)
                const uploaded: WrapperResponse<StorageFile> = await (await APIInvoke.uploadStorage(body)).json()
                if (!uploaded.ok || !uploaded.body) throw new Error(uploaded.message || UPLOAD_ERROR)
                const request: CreateAdjuntoRequest = {
                    siniestroId: siniestro.id,
                    dominio: '',
                    path: uploaded.body.url,
                    archivo: file.name,
                }
                const saved: WrapperResponse<AdjuntoDTO> = await (await APIInvoke.invokePOST('/adjuntos', request)).json()
                if (!saved.ok || !saved.body) throw new Error(saved.message || 'No se pudo registrar el archivo.')
                created.push(saved.body)
            }
            setAdjuntos((items) => [...created, ...items])
            showToast(files.length === 1 ? 'Archivo subido' : `${files.length} archivos subidos`)
        } catch (uploadError) {
            showToast(errorMessage(uploadError, UPLOAD_ERROR))
        } finally {
            setUploading(false)
        }
    }

    const deleteAdjunto = async (id: number) => {
        try {
            const payload: WrapperResponse<unknown> | null = await (await APIInvoke.invokeDELETE(`/adjuntos/${id}`)).json()
            if (!payload?.ok) throw new Error(payload?.message || 'No se pudo eliminar el archivo.')
            setAdjuntos((items) => items.filter((item) => item.id !== id))
            setConfirmDeleteId(null)
            showToast('Archivo eliminado')
        } catch (deleteError) {
            showToast(errorMessage(deleteError, 'No se pudo eliminar el archivo.'))
        }
    }

    const poliza = siniestro?.poliza || null
    const cliente = poliza?.cliente || null
    const ramo = ramoName(poliza?.ramo) || null
    const ramoKey = statusKey(ramo)
    const notas = bitacora.filter((item) => statusKey(item.tipo) !== 'adjunto')
    const asegurados: ClaimInsured[] = (siniestro?.asegurados || []).map((item) => ({
        id: item.asegurado.id,
        name: personName(item.asegurado),
        parentesco: labelize(item.parentesco),
        selected: item.selected,
    }))
    const tabs: ClaimDrawerTabItem[] = [
        { id: 'general', label: 'Datos generales' },
        { id: 'bitacora', label: 'Bitácora', count: bitacoraState === 'ready' ? notas.length : null },
        { id: 'adjuntos', label: 'Adjuntos', count: adjuntosState === 'ready' ? adjuntos.length : null },
    ]

    return {
        siniestro,
        poliza,
        cliente,
        loading,
        error,
        tab,
        tabs,
        toast,
        name: cliente ? personName(cliente) : '',
        ramo,
        aseguradora: aseguradoraName(poliza),
        status: siniestro ? claimStatusView(siniestro.status) : null,
        fechaSiniestro: formatCompactDate(siniestro?.fechaSiniestro) || '—',
        resolucion: resolutionView(siniestro?.fechaEstimadaResolucion, siniestro?.status),
        showAsegurados: ramoKey === 'vida' || ramoKey === 'salud',
        asegurados,
        vehiculo: ramoKey === 'auto' ? poliza?.vehiculo || null : null,
        vehiculoLabel: vehicleLabel(poliza?.vehiculo),
        bienTexto: ramoKey === 'diversos' ? poliza?.texto || null : null,
        confirmDelete,
        deleting,
        bitacoraState,
        bitacoraError,
        bitacoraFilter,
        visibleNotes: notas.filter((item) => bitacoraFilter === 'todo' || noteMeta(item.tipo).filter === bitacoraFilter),
        noteType,
        note,
        notePlaceholder: NOTE_TYPES.find((item) => item.id === noteType)?.placeholder,
        savingNote,
        adjuntos,
        adjuntosState,
        adjuntosError,
        uploading,
        confirmDeleteId,
        setTab,
        setNoteType,
        setNote,
        setBitacoraFilter,
        setConfirmDeleteId,
        reload: () => setReloadToken((token) => token + 1),
        askDelete: () => setConfirmDelete(true),
        cancelDelete: () => setConfirmDelete(false),
        deleteSiniestro,
        openFile,
        addNote,
        uploadFiles,
        deleteAdjunto,
    }
}
